/**
 * ClubEventsPage - Mobile-First Club Events View
 * 
 * Shows all events for a specific club with filtering and admin controls.
 * Uses EventCard components in mobile-first layout.
 * 
 * Reference: docs/design/FIGMA_IMPLEMENTATION_PLAN.md - Phase 7D
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useClub } from '../hooks/useClubs';
import { useClubEvents } from '../hooks/useEvents';
import { useAuth } from '../hooks/useAuth';
import { TopBar, EventCard, Button, EmptyState, Avatar, EventCardSkeleton } from '../components';
import { createClubEventsMobileMenu } from '../components/MobileContextMenu';
import { Plus, Calendar, Users } from 'lucide-react';

type FilterTab = 'upcoming' | 'all' | 'past';

export default function ClubEventsPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [activeFilter, setActiveFilter] = useState<FilterTab>('upcoming');

  // Fetch club details
  const { data: clubData, isLoading: clubLoading, error: clubError } = useClub(slug || '');

  // Calculate current timestamp for filtering (updated every minute)
  const [currentTimestamp, setCurrentTimestamp] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimestamp(Math.floor(Date.now() / 1000));
    }, 60000); // Update every minute

    return () => clearInterval(interval);
  }, []);

  const eventFilter = activeFilter === 'upcoming'
    ? { from: currentTimestamp }
    : activeFilter === 'past'
    ? { to: currentTimestamp }
    : undefined; // 'all' = no filter

  // Fetch events
  const { data: events = [], isLoading: eventsLoading, error: eventsError } = useClubEvents(
    clubData?.club.id || '',
    eventFilter
  );

  const loading = clubLoading || eventsLoading;
  const error = clubError || eventsError;
  const club = clubData?.club;
  const membership = clubData?.membership;
  const isLeader = membership?.role === 'leader';

  // Create mobile menu configuration
  const clubEventsMenu = slug ? createClubEventsMobileMenu(
    navigate,
    logout,
    slug,
    isLeader || false
  ) : undefined;

  if (loading) {
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Events" onBack={() => navigate(-1)} mobileMenu={clubEventsMenu} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-6 space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <EventCardSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !club || !slug) {
    const errorMessage = error 
      ? 'Failed to load events'
      : !club 
      ? 'Club not found'
      : 'Invalid club';
      
    return (
      <div className="flex flex-col bg-oro-bg">
        <TopBar title="Events" onBack={() => navigate(-1)} mobileMenu={clubEventsMenu} />
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-12">
            <EmptyState
              icon="📅"
              title="Unable to load events"
              message={errorMessage}
              actionLabel="Back to Club"
              onAction={() => navigate(`/clubs/${slug}`)}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-oro-bg">
      <TopBar 
        title="Events" 
        onBack={() => navigate(`/clubs/${slug}`)} 
        mobileMenu={clubEventsMenu}
      />
      
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-6 space-y-6">
          {/* Club Identity Card */}
          <div className="bg-oro-surface border border-oro-border rounded-xl p-4 shadow-card">
            <div className="flex items-center gap-3">
              <Avatar
                name={club.name}
                profile_image_url={club.hero_image_url}
                size="md"
                className="shrink-0"
              />
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-text-primary truncate">
                  {club.name}
                </h2>
                <div className="flex items-center gap-1 text-text-muted text-sm">
                  <Users className="w-3 h-3" />
                  <span>Club Members</span>
                </div>
              </div>
              {isLeader && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => navigate(`/clubs/${slug}/events/create`)}
                  className="shrink-0"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Create
                </Button>
              )}
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="flex gap-1 bg-oro-surface-alt border border-oro-border rounded-xl p-1">
            {[
              { key: 'upcoming' as const, label: 'Upcoming' },
              { key: 'all' as const, label: 'All' },
              { key: 'past' as const, label: 'Past' }
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveFilter(tab.key)}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeFilter === tab.key
                    ? 'bg-oro-surface text-text-primary shadow-sm'
                    : 'text-text-muted hover:text-text-secondary'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Events Count Header */}
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-text-muted" />
            <span className="text-text-secondary text-sm">
              {events.length} {activeFilter === 'upcoming' ? 'upcoming' : activeFilter === 'past' ? 'past' : ''} 
              {events.length === 1 ? ' event' : ' events'}
            </span>
          </div>

          {/* Events List */}
          {events.length > 0 ? (
            <div className="space-y-3 pb-20">
              {events.map((event) => {
                // Format event data for EventCard component
                const eventDate = new Date(event.starts_at * 1000);
                const formattedDate = eventDate.toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric'
                });
                
                return (
                  <EventCard
                    key={event.id}
                    event={{
                      id: event.id,
                      title: event.title,
                      date: formattedDate,
                      location: event.location_name || 'Location TBD',
                      attendees: event.rsvp_count || 0,
                      imageUrl: event.hero_image_url || '' // Backend COALESCE handles event → club fallback
                    }}
                    onClick={() => navigate(`/event/${event.id}`)}
                  />
                );
              })}
            </div>
          ) : (
            <div className="py-12">
              <EmptyState
                icon="📅"
                title={
                  activeFilter === 'upcoming' 
                    ? 'No upcoming events'
                    : activeFilter === 'past'
                    ? 'No past events'  
                    : 'No events yet'
                }
                message={
                  activeFilter === 'upcoming'
                    ? 'Check back later for new events'
                    : activeFilter === 'past'
                    ? 'This club hasn\'t hosted any events yet'
                    : 'This club hasn\'t created any events yet'
                }
                actionLabel={isLeader ? 'Create Event' : undefined}
                onAction={isLeader ? () => navigate(`/clubs/${slug}/events/create`) : undefined}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Floating Create Button for Mobile */}
      {isLeader && (
        <button
          onClick={() => navigate(`/clubs/${slug}/events/create`)}
          className="fixed bottom-20 right-4 w-14 h-14 bg-accent hover:bg-accent/90 text-black rounded-full shadow-float flex items-center justify-center transition-all duration-200 hover:shadow-float-lg"
          aria-label="Create event"
        >
          <Plus className="w-6 h-6" />
        </button>
      )}
    </div>
  );
}